import { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RouteAccessGate } from '@/components/routes/RouteAccessGate';
import { useFeatures } from '@/core/features/useFeatures';
import { POS_ROUTE_FEATURES } from '@/config/routeFeatures';
import { resolveOrgRoutePermission } from '@/utils/navigation';
import { useAppSelector } from '@/hooks/useAppSelector';
import type { RootState } from '@/store';
import {
  isOnboardingDone,
  isPlatformOnboardingRequired,
  isFattahOneProductSelectionRequired,
  isProductSelectionDone,
} from '@/modules/onboarding/onboardingState';
import { resolveLoginRedirect } from '@/routes/ModuleScopeGuard';
import { useActiveModule } from '@/hooks/useActiveModule';
import { getPostLoginPath } from '@/utils/moduleScope';
import { evaluateOrgRouteDenial, evaluatePosRouteDenial } from '@/utils/routeAccessPolicy';
import { getLauncherModules, isOrgOwner, resolveOwnerHomePath, resolveSingleAppHomePath } from '@/utils/moduleAccess';
import { canAccessWorkspacePath } from '@/utils/moduleNavigation';
import { usePermissions } from '@/hooks/usePermissions';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { resolveModuleAdminRoutePermission } from '@/config/moduleDefaults';
import { withModuleScopedMasterRoutes } from '@/config/moduleSharedPaths';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import type { ErpModule } from '@/api/platform.api';

type RoleGuard = 'master' | 'org' | 'any';

const BASE_ORG_ROUTE_PERMISSIONS: Record<string, string | string[] | null> = {
  '/pos/dashboard': ['DASHBOARD_VIEW', 'BILLING_VIEW'],
  '/org/profile': 'ORG_VIEW',
  '/pos/stores': 'STORE_VIEW',
  '/org/employees': 'EMPLOYEE_VIEW',
  '/org/roles': 'ROLE_VIEW',
  '/pos/products': 'PRODUCT_VIEW',
  '/pos/categories': 'PRODUCT_VIEW',
  '/pos/brands': 'PRODUCT_VIEW',
  '/pos/units': 'PRODUCT_VIEW',
  '/pos/taxes': 'PRODUCT_VIEW',
  '/pos/tax-rules': 'PRODUCT_VIEW',
  '/pos/store-products': 'PRODUCT_VIEW',
  '/pos/invoices': 'BILLING_VIEW',
  '/pos/inventory': 'INVENTORY_VIEW',
  '/pos/inventory/ledger': 'INVENTORY_VIEW',
  '/warehouse': 'WAREHOUSE_VIEW',
  '/warehouse/operations': 'WAREHOUSE_VIEW',
  '/warehouse/mobile': 'WAREHOUSE_VIEW',
  '/warehouse/setup': 'WAREHOUSE_ADMIN',
  '/warehouse/picks': 'WAREHOUSE_PICK',
  '/warehouse/transfers': 'WAREHOUSE_TRANSFER',
  '/warehouse/cycle-counts': 'WAREHOUSE_VIEW',
  '/warehouse/waves': 'WAREHOUSE_PICK',
  '/warehouse/dock': 'WAREHOUSE_VIEW',
  '/warehouse/cross-dock': 'WAREHOUSE_RECEIVE',
  '/warehouse/labor': 'WAREHOUSE_VIEW',
  '/pos/product-alerts': 'INVENTORY_VIEW',
  '/pos/suppliers': 'INVENTORY_VIEW',
  '/procurement': 'PROCUREMENT_VIEW',
  '/procurement/purchase-requests': 'PROCUREMENT_VIEW',
  '/procurement/rfqs': 'PROCUREMENT_VIEW',
  '/procurement/quotations': 'PROCUREMENT_VIEW',
  '/procurement/purchase-orders': 'PROCUREMENT_VIEW',
  '/procurement/goods-receipts': 'PROCUREMENT_RECEIVE',
  '/procurement/vendor-bills': 'PROCUREMENT_VENDOR_BILL',
  '/procurement/approvals': ['PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_PO_APPROVE'],
  '/procurement/reports': 'PROCUREMENT_ANALYTICS_VIEW',
  '/procurement/setup': 'PROCUREMENT_MANAGE',
  '/procurement/contracts': 'PROCUREMENT_VIEW',
  '/procurement/catalogs': 'PROCUREMENT_VIEW',
  '/pos/customers': 'CUSTOMER_VIEW',
  '/crm': 'CRM_VIEW',
  '/crm/leads': 'CRM_VIEW',
  '/crm/contacts': 'CRM_VIEW',
  '/crm/companies': 'CRM_VIEW',
  '/crm/deals': 'CRM_VIEW',
  '/crm/pipelines': 'CRM_PIPELINE_MANAGE',
  '/crm/followups': 'CRM_VIEW',
  '/crm/tasks': 'CRM_VIEW',
  '/crm/campaigns': 'CRM_CAMPAIGN_MANAGE',
  '/crm/analytics': 'CRM_ANALYTICS_VIEW',
  '/crm/reports': 'CRM_ANALYTICS_VIEW',
  '/crm/setup': 'CRM_MANAGE',
  '/lead-gen': 'LEAD_GEN_VIEW',
  '/lead-gen/forms': 'LEAD_GEN_MANAGE',
  '/lead-gen/landing-pages': 'LEAD_GEN_MANAGE',
  '/lead-gen/campaigns': 'LEAD_GEN_CAMPAIGN',
  '/lead-gen/visitor-tracking': 'LEAD_GEN_VIEW',
  '/lead-gen/scoring': 'LEAD_GEN_MANAGE',
  '/lead-gen/routing': 'LEAD_GEN_MANAGE',
  '/lead-gen/ab-tests': 'LEAD_GEN_MANAGE',
  '/lead-gen/webhooks': 'LEAD_GEN_VIEW',
  '/lead-gen/enrichment': 'LEAD_GEN_MANAGE',
  '/lead-gen/consent': 'LEAD_GEN_VIEW',
  '/lead-gen/reports': 'LEAD_GEN_ANALYTICS',
  '/lead-gen/sources': 'LEAD_GEN_VIEW',
  '/social': 'SOCIAL_VIEW',
  '/social/inbox': ['SOCIAL_VIEW', 'SOCIAL_INBOX_REPLY'],
  '/social/channels': 'SOCIAL_CHANNEL_MANAGE',
  '/social/publishing': 'SOCIAL_PUBLISH',
  '/social/calendar': 'SOCIAL_PUBLISH',
  '/social/analytics': 'SOCIAL_ANALYTICS_VIEW',
  '/social/automation': 'SOCIAL_VIEW',
  '/social/crm-leads': 'SOCIAL_VIEW',
  '/social/teams': 'SOCIAL_VIEW',
  '/social/comments': 'SOCIAL_INBOX_REPLY',
  '/social/connect': 'SOCIAL_CHANNEL_MANAGE',
  '/social/settings': 'SOCIAL_MANAGE',
  '/social/platforms': 'SOCIAL_VIEW',
  '/social/approvals': 'SOCIAL_PUBLISH',
  '/social/webhooks': 'SOCIAL_VIEW',
  '/social/listening': 'SOCIAL_VIEW',
  '/social/campaigns': 'SOCIAL_CAMPAIGN_MANAGE',
  '/social/reviews': 'SOCIAL_REVIEW_MANAGE',
  '/social/reports': 'SOCIAL_ANALYTICS_VIEW',
  '/social/setup': 'SOCIAL_MANAGE',
  '/hr': 'HR_VIEW',
  '/hr/employees': 'HR_EMPLOYEE_MANAGE',
  '/hr/attendance': 'HR_ATTENDANCE_MANAGE',
  '/hr/leaves': 'HR_LEAVE_MANAGE',
  '/hr/leave-policies': 'HR_LEAVE_MANAGE',
  '/hr/shifts': 'HR_ATTENDANCE_MANAGE',
  '/hr/onboarding': 'HR_MANAGE',
  '/hr/documents': 'HR_MANAGE',
  '/hr/compensation': 'HR_PAYROLL_MANAGE',
  '/hr/travel': 'HR_MANAGE',
  '/hr/succession': 'HR_MANAGE',
  '/hr/admin': 'HR_ADMIN',
  '/hr/approvals': 'HR_APPROVAL_MANAGE',
  '/hr/payroll': 'HR_PAYROLL_MANAGE',
  '/hr/statutory': 'HR_PAYROLL_MANAGE',
  '/hr/recruitment': 'HR_RECRUITMENT_MANAGE',
  '/hr/appraisal': 'HR_MANAGE',
  '/hr/claims': 'HR_CLAIM_MANAGE',
  '/hr/training': 'HR_TRAINING_MANAGE',
  '/hr/reports': 'HR_ANALYTICS_VIEW',
  '/hr/setup': 'HR_MANAGE',
  '/hr/workflows': 'HR_MANAGE',
  '/hr/org': 'HR_MANAGE',
  '/hr/mss': 'HR_APPROVAL_MANAGE',
  '/hr/ess': 'HR_VIEW',
  '/hr/feedback360': 'HR_MANAGE',
  '/hr/devices': 'HR_ATTENDANCE_MANAGE',
  '/desk': 'DESK_VIEW',
  '/desk/tickets': 'DESK_VIEW',
  '/desk/support-360': 'DESK_VIEW',
  '/desk/inbox': 'DESK_VIEW',
  '/desk/livechat': 'DESK_VIEW',
  '/desk/projects': 'DESK_PROJECT_MANAGE',
  '/desk/boards': 'DESK_PROJECT_MANAGE',
  '/desk/knowledgebase': 'DESK_VIEW',
  '/desk/reports': 'DESK_ANALYTICS_VIEW',
  '/desk/automation': 'DESK_AUTOMATION_MANAGE',
  '/desk/setup': 'DESK_WORKFLOW_MANAGE',
  '/desk/setup/workflows': 'DESK_WORKFLOW_MANAGE',
  '/desk/canned-responses': 'DESK_WORKFLOW_MANAGE',
  '/finance': 'FINANCE_VIEW',
  '/finance/receivables': 'FINANCE_VIEW',
  '/finance/payables': 'FINANCE_VIEW',
  '/finance/payables/ap': 'FINANCE_AP_MANAGE',
  '/finance/tax': 'FINANCE_VIEW',
  '/finance/control': 'FINANCE_VIEW',
  '/finance/setup/wizard': 'FINANCE_SETTINGS_MANAGE',
  '/finance/health': 'FINANCE_VIEW',
  '/finance/close': 'FINANCE_MANAGE',
  '/finance/control/rules': 'FINANCE_SETTINGS_MANAGE',
  '/finance/treasury/forecast': 'FINANCE_TREASURY_MANAGE',
  '/finance/collections': 'FINANCE_INVOICE_MANAGE',
  '/finance/automation': 'FINANCE_MANAGE',
  '/finance/analytics': 'FINANCE_REPORT_VIEW',
  '/finance/compliance': 'FINANCE_TAX_MANAGE',
  '/finance/audit': 'FINANCE_MANAGE',
  '/finance/documents': 'FINANCE_AP_MANAGE',
  '/finance/integrations': 'FINANCE_VIEW',
  '/finance/accounting': 'FINANCE_MANAGE',
  '/finance/ledger': 'FINANCE_JOURNAL_MANAGE',
  '/finance/invoices': 'FINANCE_INVOICE_MANAGE',
  '/finance/payments': 'FINANCE_PAYMENT_MANAGE',
  '/finance/banking': 'FINANCE_BANKING_MANAGE',
  '/finance/taxes': 'FINANCE_TAX_MANAGE',
  '/finance/expenses': 'FINANCE_EXPENSE_MANAGE',
  '/finance/assets': 'FINANCE_ASSET_MANAGE',
  '/finance/budgets': 'FINANCE_MANAGE',
  '/finance/treasury': 'FINANCE_TREASURY_MANAGE',
  '/finance/revenue': 'FINANCE_MANAGE',
  '/finance/workflows': 'FINANCE_SETTINGS_MANAGE',
  '/finance/workflows/designer': 'FINANCE_SETTINGS_MANAGE',
  '/finance/reports': 'FINANCE_REPORT_VIEW',
  '/finance/reports/builder': 'FINANCE_REPORT_VIEW',
  '/finance/setup': 'FINANCE_SETTINGS_MANAGE',
  '/projects': 'PROJECT_VIEW',
  '/projects/list': 'PROJECT_VIEW',
  '/projects/tasks': 'PROJECT_TASK_MANAGE',
  '/projects/boards': 'PROJECT_VIEW',
  '/projects/sprints': 'PROJECT_SPRINT_MANAGE',
  '/projects/timesheets': 'PROJECT_TIMESHEET_MANAGE',
  '/projects/portfolio': 'PROJECT_PORTFOLIO_MANAGE',
  '/projects/issues': 'PROJECT_ISSUE_MANAGE',
  '/projects/gantt': 'PROJECT_VIEW',
  '/projects/calendar': 'PROJECT_VIEW',
  '/projects/workload': 'PROJECT_ANALYTICS_VIEW',
  '/projects/reports': 'PROJECT_ANALYTICS_VIEW',
  '/projects/automation': 'PROJECT_AUTOMATION_MANAGE',
  '/projects/setup': 'PROJECT_MANAGE',
  '/pos/discounts': 'PRODUCT_VIEW',
  '/pos/terminal-reports': 'REPORT_VIEW',
  '/org/subscription': 'SUBSCRIPTION_MANAGE',
  '/org/administration/billing': 'SUBSCRIPTION_MANAGE',
  '/org/workspace/settings': null,
  '/org/workspace/subscription': null,
  '/org/settings': null,
  '/org/administration': 'ORG_MANAGE',
};

const ORG_ROUTE_PERMISSIONS = withModuleScopedMasterRoutes(BASE_ORG_ROUTE_PERMISSIONS);

const POS_ROUTE_PERMISSIONS: Record<string, string | string[] | null> = {
  '/pos': 'BILLING_CREATE',
  '/pos/held': 'BILLING_VIEW',
  '/pos/shift': 'BILLING_CREATE',
  '/pos/returns': 'BILLING_RETURN',
  '/pos/analytics': ['BILLING_VIEW', 'REPORT_VIEW'],
  '/pos/setup': ['POS_ADMIN', 'STORE_MANAGE'],
  '/pos/terminal-reports': 'REPORT_VIEW',
  '/pos/shortcuts': 'BILLING_CREATE',
};

interface ProtectedRouteProps {
  role?: RoleGuard;
  permission?: string | string[];
}

type ProtectedRouteOutletProps = ProtectedRouteProps & {
  user: NonNullable<RootState['auth']['user']>;
  entryModule: ErpModule | null | undefined;
};

function ProtectedRouteOutlet({ role = 'any', permission, user, entryModule }: ProtectedRouteOutletProps) {
  const location = useLocation();
  const { hasFeature, loading: featuresLoading } = useFeatures();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const { has } = usePermissions();

  const routePermission =
    permission ??
    (role === 'org'
      ? (resolveModuleAdminRoutePermission(location.pathname) ??
        ORG_ROUTE_PERMISSIONS[location.pathname] ??
        resolveOrgRoutePermission(location.pathname))
      : role === 'any'
        ? POS_ROUTE_PERMISSIONS[location.pathname]
        : undefined);

  const perms = user.permissions ?? [];
  const owner = isOrgOwner(perms, user.roleCode);
  const scopedModule: ErpModule | null =
    !user.masterAdmin && role === 'org'
      ? owner
        ? (resolveModuleFromPath(location.pathname) ?? entryModule ?? null)
        : (entryModule ?? null)
      : null;

  const accessDenial = useMemo(() => {
    if (user.masterAdmin) return null;

    if (role === 'org' && !location.pathname.startsWith('/org/locked')) {
      return evaluateOrgRouteDenial({
        pathname: location.pathname,
        permissions: perms,
        masterAdmin: false,
        entryModule,
        scopedModule,
        routePermission,
        hasFeature,
        featuresLoading,
      });
    }

    if (role === 'any') {
      return evaluatePosRouteDenial({
        pathname: location.pathname,
        permissions: perms,
        masterAdmin: false,
        routePermission,
        hasFeature,
        featuresLoading,
        posRouteFeatures: POS_ROUTE_FEATURES,
        entryModule,
      });
    }

    return null;
  }, [
    user.masterAdmin,
    role,
    location.pathname,
    perms,
    entryModule,
    scopedModule,
    routePermission,
    hasFeature,
    featuresLoading,
  ]);

  const launcherCodes = useMemo(
    () =>
      getLauncherModules(enabledModules, perms, accessibleModules, user.roleCode, has).map(
        (entry) => entry.erpModule,
      ),
    [enabledModules, perms, accessibleModules, user.roleCode, has],
  );

  const canAccessPath = useMemo(
    () => (pathname: string) =>
      canAccessWorkspacePath(pathname, enabledModules, launcherCodes, accessibleModules),
    [accessibleModules, enabledModules, launcherCodes],
  );

  const fallbackPath = entryModule ? getPostLoginPath(entryModule, MODULE_HUB_PATH) : MODULE_HUB_PATH;

  return (
    <RouteAccessGate
      denied={accessDenial != null}
      message={accessDenial?.message}
      fallbackPath={accessDenial?.fallbackPath ?? fallbackPath}
      canAccessPath={canAccessPath}
    />
  );
}

export function ProtectedRoute({ role = 'any', permission }: ProtectedRouteProps) {
  const { isAuthenticated, user, initialized, entryModule, enabledModules } = useAppSelector((s) => s.auth);
  const location = useLocation();
  const activeModule = useActiveModule();

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const loginPath = resolveLoginRedirect(location.pathname, entryModule, activeModule);
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (role === 'master' && !user.masterAdmin) {
    return <Navigate to="/pos/dashboard" replace />;
  }

  if (role === 'org' && user.masterAdmin) {
    return <Navigate to="/master" replace />;
  }

  if (
    role === 'org' &&
    !user.masterAdmin &&
    !entryModule &&
    !isOrgOwner(user.permissions ?? [], user.roleCode)
  ) {
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }

  if (isPlatformOnboardingRequired() && location.pathname === '/onboarding/pos') {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    role === 'org' &&
    !user.masterAdmin &&
    !entryModule &&
    isOrgOwner(user.permissions ?? []) &&
    isPlatformOnboardingRequired()
  ) {
    if (isFattahOneProductSelectionRequired() && location.pathname !== '/onboarding/products') {
      return <Navigate to="/onboarding/products" replace />;
    }
    if (
      isPlatformOnboardingRequired() &&
      isProductSelectionDone() &&
      location.pathname !== '/onboarding' &&
      location.pathname !== '/onboarding/pos'
    ) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  if (isOnboardingDone() && location.pathname.startsWith('/onboarding')) {
    return <Navigate to={resolveOwnerHomePath(enabledModules)} replace />;
  }

  return <ProtectedRouteOutlet role={role} permission={permission} user={user} entryModule={entryModule} />;
}

export function PublicOnlyRoute() {
  const { isAuthenticated, user, entryModule, enabledModules, accessibleModules } = useAppSelector((s) => s.auth);
  const { has } = usePermissions();
  const permissions = user?.permissions ?? [];
  if (isAuthenticated && user) {
    if (user.masterAdmin) {
      return <Navigate to="/master" replace />;
    }
    if (entryModule) {
      return <Navigate to={getPostLoginPath(entryModule, resolveOwnerHomePath(enabledModules))} replace />;
    }
    if (isFattahOneProductSelectionRequired()) {
      return <Navigate to="/onboarding/products" replace />;
    }
    if (isPlatformOnboardingRequired()) {
      return <Navigate to="/onboarding" replace />;
    }
    const soleHome = resolveSingleAppHomePath(
      enabledModules,
      has,
      permissions,
      accessibleModules,
      user.roleCode,
    );
    if (soleHome) {
      return <Navigate to={soleHome} replace />;
    }
    if (isOrgOwner(user.permissions ?? [], user.roleCode)) {
      return <Navigate to={resolveOwnerHomePath(enabledModules)} replace />;
    }
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }
  return <Outlet />;
}

